import { Avatar, Tag, Typography, theme as antdTheme } from 'antd';
import type { ReactNode } from 'react';
import { fileUrl } from '@/shared/lib/file-url';

type Tone = 'success' | 'warning' | 'default' | 'processing' | 'error';

interface Props {
  eyebrow: string;
  title: string;
  /** Pre-rendered meta row — typically a few `<Tag>` and `<Typography.Text>` items in a Space. */
  subtitle?: ReactNode;
  description?: ReactNode;
  statusPill?: { label: string; tone: Tone };
  avatar?: {
    src?: string | null;
    fallback: string;
    shape?: 'circle' | 'square';
    size?: number;
  };
  actions?: ReactNode;
}

export function ProfileHero({
  eyebrow,
  title,
  subtitle,
  description,
  statusPill,
  avatar,
  actions,
}: Props) {
  const { token } = antdTheme.useToken();
  const avatarSize = avatar?.size ?? 88;
  const resolvedSrc = avatar?.src ? fileUrl(avatar.src) : null;

  return (
    <header style={{ display: 'grid', gap: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: 1, minWidth: 0 }}>
          {avatar && (
            <Avatar
              size={avatarSize}
              shape={avatar.shape ?? 'circle'}
              src={resolvedSrc ?? undefined}
              style={{
                background: token.colorFillSecondary,
                color: token.colorTextSecondary,
                fontSize: avatarSize / 2.6,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {avatar.fallback}
            </Avatar>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}
            >
              {eyebrow}
            </Typography.Text>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginTop: 4,
                flexWrap: 'wrap',
              }}
            >
              <Typography.Title
                level={1}
                className="profile-hero-title"
                style={{ margin: 0, fontSize: 30, lineHeight: 1.15, fontWeight: 600 }}
              >
                {title}
              </Typography.Title>
              {statusPill && (
                <Tag color={statusPill.tone} style={{ fontSize: 12, marginInlineEnd: 0 }}>
                  {statusPill.label}
                </Tag>
              )}
            </div>
            {subtitle && <div style={{ marginTop: 6 }}>{subtitle}</div>}
            {description && (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                {description}
              </Typography.Text>
            )}
          </div>
        </div>
        {actions && (
          <div
            className="profile-hero-actions"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            {actions}
          </div>
        )}
      </div>
      <div style={{ height: 1, background: token.colorBorderSecondary }} />
    </header>
  );
}
